import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  buttonPrimary,
  buttonSecondary,
  delay,
  tone,
} from '@/components/agents/primitives';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';

import { scrollToSection } from '@/utils/scrollToSection';

import { COMMISSION, COOKIE_DAYS, formatUsd, REWARDFUL } from './kit';

const MONTHS = [
  'Month 1',
  'Month 2',
  'Month 3',
  'Month 4',
  'Month 5',
  'Month 6',
];

export default function AffiliatesHero({
  example,
}: {
  /** The plan the example card is priced on. */
  example: { name: string; monthly: number };
}) {
  const perMonth = example.monthly * COMMISSION;

  const facts = [
    { value: '30%', label: 'of every payment, every month' },
    { value: `${COOKIE_DAYS} days`, label: 'for a click to turn into a sale' },
    { value: 'Monthly', label: 'payouts to your PayPal' },
    { value: 'Free', label: 'to join' },
  ];

  return (
    <header className='relative'>
      <LandingPageNavigation />

      <div className='mx-auto grid max-w-7xl gap-x-14 gap-y-14 px-6 pt-12 sm:pt-20 lg:grid-cols-12 lg:items-center lg:px-8'>
        <div className='lg:col-span-7'>
          <p
            className={clsxm(
              styles.enter,
              'inline-flex items-center gap-2.5 rounded-full bg-white py-1 pe-3.5 ps-1 text-sm font-medium shadow-[0_1px_2px_rgba(15,15,40,0.05)] ring-1 ring-[#0f0f28]/[0.08] dark:bg-white/[0.05] dark:ring-white/10',
              tone.body,
            )}
          >
            <span className='rounded-full bg-primary-600 px-2 py-0.5 text-xs font-semibold text-white'>
              Affiliate program
            </span>
            Free to join, paid monthly
          </p>

          <h1
            className={clsxm(
              styles.display,
              styles.enter,
              'mt-8 text-[2.875rem] font-semibold leading-[1.02] sm:text-7xl sm:leading-[1] lg:text-[5rem]',
              tone.ink,
            )}
            style={delay(60)}
          >
            Share the AI you use.{' '}
            <span className='text-primary-600 dark:text-primary-400'>
              Earn 30%.
            </span>
          </h1>

          <div className={styles.enter} style={delay(160)}>
            <p
              className={clsxm(
                'mt-7 max-w-xl text-lg leading-8 sm:text-[1.3125rem] sm:leading-[1.6]',
                tone.body,
              )}
            >
              Recommend Easy-Peasy.AI to your audience or clients. For every
              paying customer you refer, you earn 30% of each payment they make,
              month after month.
            </p>

            <div className='mt-9 flex flex-wrap items-center gap-3'>
              <a href={REWARDFUL.signup} className={buttonPrimary}>
                Become an affiliate
                <ArrowGlyph />
              </a>
              <a
                href='#calculator'
                onClick={(event) => scrollToSection(event, 'calculator')}
                className={buttonSecondary}
              >
                Estimate your earnings
              </a>
            </div>
          </div>
        </div>

        <figure
          className={clsxm(styles.enter, 'lg:col-span-5')}
          style={delay(240)}
        >
          <div
            className={clsxm(
              'rounded-[28px] p-6 shadow-[0_40px_80px_-40px_rgba(30,27,75,0.55)] sm:p-8',
              tone.surface,
              tone.line,
            )}
          >
            <figcaption
              className={clsxm('text-[13px] font-medium', tone.quiet)}
            >
              Example: one referral
            </figcaption>
            <p className={clsxm('mt-3 text-[15px] leading-6', tone.body)}>
              Someone signs up through your link and subscribes to{' '}
              <span className={clsxm('font-semibold', tone.ink)}>
                {example.name}
              </span>{' '}
              at {formatUsd(example.monthly)} a month.
            </p>
            <p
              dir='ltr'
              className={clsxm(
                styles.display,
                'mt-6 text-5xl font-semibold tabular-nums',
                tone.ink,
              )}
            >
              {formatUsd(perMonth)}
              <span
                className={clsxm(
                  'ms-1.5 text-lg font-medium tracking-normal',
                  tone.quiet,
                )}
              >
                / month to you
              </span>
            </p>

            <p className={clsxm('mt-7 text-[13px] font-medium', tone.quiet)}>
              Running total, month by month
            </p>
            <ol className='mt-3 grid grid-cols-6 items-end gap-2'>
              {MONTHS.map((month, index) => (
                <li key={month} className='flex flex-col items-center gap-2'>
                  <span
                    className='w-full rounded-t-lg bg-gradient-to-t from-primary-600 to-primary-400'
                    style={{ height: `${28 + index * 14}px` }}
                    aria-hidden='true'
                  />
                  <span
                    className={clsxm('text-[11px] tabular-nums', tone.quiet)}
                  >
                    {index + 1}
                  </span>
                </li>
              ))}
            </ol>
            <p
              className={clsxm(
                'mt-4 flex items-baseline justify-between gap-4 border-t border-[#0f0f28]/[0.07] pt-4 text-sm dark:border-white/[0.08]',
                tone.body,
              )}
            >
              After 6 months
              <span
                className={clsxm('font-semibold tabular-nums', tone.ink)}
                dir='ltr'
              >
                {formatUsd(perMonth * 6)}
              </span>
            </p>
          </div>
        </figure>
      </div>

      <div className='mx-auto mt-16 max-w-7xl px-6 sm:mt-20 lg:px-8'>
        <dl className='grid grid-cols-2 gap-px overflow-hidden rounded-[22px] bg-[#e7e7ef] ring-1 ring-[#0f0f28]/[0.07] lg:grid-cols-4 dark:bg-[#26253a] dark:ring-white/[0.08]'>
          {facts.map(({ value, label }) => (
            <div
              key={value}
              className={clsxm('flex flex-col px-5 py-5 sm:px-7', tone.surface)}
            >
              <dt className={clsxm('mt-2 text-sm leading-5', tone.quiet)}>
                {label}
              </dt>
              <dd
                className={clsxm(
                  styles.display,
                  'order-first text-3xl font-semibold tabular-nums sm:text-[2.25rem] sm:leading-none',
                  tone.ink,
                )}
              >
                {value}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </header>
  );
}
